import React, { useState } from 'react';
import { Button, Color, Icon, Text } from '@bb/elements-web';
import styled from 'styled-components';

interface AlertBannerProps {
  /** Message to display in the banner */
  message: string;
  /** Whether the logged-in user is able to close the banner or not */
  canCloseBanner: boolean;
  /** Button text to display when the action button is shown, will display the button if provided */
  buttonText?: string;
  /** Function to run when the action button is clicked */
  onButtonClick?: () => void;
}

/** AlertBanner component to display messages with optional action button */
const AlertBanner: React.FC<AlertBannerProps> = ({
  message,
  canCloseBanner,
  buttonText,
  onButtonClick,
}) => {
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const handleCloseBanner = () => setIsBannerVisible(false);

  if (!isBannerVisible) return null;

  return (
    <Background>
      <BannerContainer>
        <Icon icon={'closeFilled'} color="danger" size={16} />
        <BannerText>{message}</BannerText>
        {!!buttonText && (
          <Button
            size="small"
            shape="default"
            type="primary"
            onClick={onButtonClick}
            dataCy={'pay-now-button-banner'}
            styleOverride={customBannerButtonStyle}
          >
            {buttonText}
          </Button>
        )}
        {canCloseBanner && (
          <Button
            size="small"
            shape="default"
            type="secondary"
            onClick={handleCloseBanner}
            dataCy={'close-banner'}
            styleOverride={CloseButtonStyle}
          >
            <Icon icon={'closeOutlined'} size={14} color="black" />
          </Button>
        )}
      </BannerContainer>
    </Background>
  );
};

export default AlertBanner;

const Background = styled.div`
  display: flex;
  background-color: #fff2f0; // TODO: convert to constant
  border: 1px solid #ffccc7; // TODO: convert to constant
  border-radius: 8px;
`;

const BannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 12px 14px;
  align-items: center;
`;

const BannerText = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${Color.PRIMARY_TEXT};
  align-self: center;
  margin-left: 12px;
  margin-right: 12px;
  flex: 1;
`;

/**
 * Custom style because we need the banner to be pretty small height wise.
 * This makes it so there's only horizontal padding and the button shrinks
 * to fit the content horizontally as well.
 */
const customBannerButtonStyle = {
  padding: '0px 16px',
  minWidth: 0,
};

const CloseButtonStyle = {
  justifyContent: 'center',
  width: 32,
  height: 32,
  maxWidth: 32,
  minWidth: 32,
  gap: 8,
  border: 'none',
};
